import React, { useState } from 'react';
import { format, parseISO, isValid } from 'date-fns';
import {
  Calendar,
  User,
  X,
  Download,
  Printer,
  TrendingUp,
  AlertCircle,
  Info
} from 'lucide-react';
import {
  TestResult as AppTestResult,
  TestComponent as AppTestComponent,
  TestComponentStatus as AppTestComponentStatus
} from '../../types/test-result-types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import TestTrendsChart from './test-trends-chart';
import ErrorBoundary from '@/components/ui/ErrorBoundary';

interface EnhancedResultDetailsProps {
  result: AppTestResult;
  onClose: () => void;
  className?: string;
}

/**
 * Enhanced Result Details Modal
 * Displays comprehensive test result information with trends and export options
 */
export const EnhancedResultDetails: React.FC<EnhancedResultDetailsProps> = ({
  result,
  onClose,
  className = ''
}) => {
  const [selectedTab, setSelectedTab] = useState<'results' | 'trends' | 'interpretation'>('results');

  // Get status badge styling
  const getStatusBadge = (status: AppTestComponentStatus | undefined) => {
    let badgeProps: { variant: 'secondary' | 'outline' | 'warning' | 'destructive'; label: string } = {
      variant: 'secondary',
      label: 'Unknown'
    };

    switch (status) {
      case 'normal':
        badgeProps = { variant: 'outline', label: 'Normal' };
        break;
      case 'high':
      case 'low':
      case 'abnormal':
        badgeProps = { variant: 'warning', label: status.charAt(0).toUpperCase() + status.slice(1) };
        break;
      case 'critical':
        badgeProps = { variant: 'destructive', label: 'Critical' };
        break;
      default:
        badgeProps = { variant: 'secondary', label: status || 'N/A' };
        break;
    }

    return (
      <Badge variant={badgeProps.variant}>
        {badgeProps.label}
      </Badge>
    );
  };

  // Count abnormal results
  const abnormalCount = result.components?.filter((r: AppTestComponent) =>
    ['abnormal', 'high', 'low', 'critical'].includes(r.status || '')
  ).length || 0;

  // Handle export to PDF
  const handleExportPDF = () => {
    // In a real implementation, this would generate a PDF
    alert('PDF export functionality would be implemented here');
  };

  // Handle print
  const handlePrint = () => {
    window.print();
  };

  // Generate simplified medical explanation
  const getSimplifiedExplanation = (component: AppTestComponent) => {
    const explanations: Record<string, string> = {
      'WBC': 'White Blood Cells help fight infections. Normal levels indicate a healthy immune system.',
      'RBC': 'Red Blood Cells carry oxygen throughout your body. Normal levels mean good oxygen delivery.',
      'Hemoglobin': 'Protein in red blood cells that carries oxygen. Low levels may indicate anemia.',
      'Platelets': 'Help with blood clotting. Normal levels ensure proper clotting function.',
      'Glucose': 'Blood sugar level. High levels may indicate diabetes or prediabetes.',
      'Cholesterol': 'Fat-like substance in blood. High levels increase heart disease risk.',
      'HDL': 'Good cholesterol that protects against heart disease. Higher is better.',
      'LDL': 'Bad cholesterol that can clog arteries. Lower is better.',
      'Triglycerides': 'Type of fat in blood. High levels increase heart disease risk.',
      'Creatinine': 'Waste product filtered by kidneys. High levels may indicate kidney problems.',
      'ALT': 'Liver enzyme. High levels may indicate liver damage or disease.',
      'AST': 'Liver enzyme. High levels may indicate liver or heart problems.',
    };

    return explanations[component.name || ''] || 'No explanation available.';
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/50 ${className}`}
      onClick={onClose}
    >
      <Card
        className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col m-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <CardHeader className="border-b">
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-xl">{result.testName}</CardTitle>
              <div className="text-sm text-muted-foreground mt-1 flex flex-wrap gap-2">
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {result.orderedDate && isValid(parseISO(result.orderedDate))
                    ? format(parseISO(result.orderedDate), 'MMMM d, yyyy')
                    : 'Date not available'}
                </span>
                {result.reviewedBy && (
                  <span className="flex items-center gap-1">
                    <User className="h-4 w-4" />
                    {result.reviewedBy}
                  </span>
                )}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>

        {/* Abnormal Results Alert */}
        {abnormalCount > 0 && (
          <Alert className="m-4 mb-0">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {abnormalCount} abnormal result{abnormalCount !== 1 ? 's' : ''} found.
              Please consult with your healthcare provider.
            </AlertDescription>
          </Alert>
        )}

        {/* Content Tabs */}
        <div className="flex-1 overflow-hidden p-4">
          <ErrorBoundary>
            <Tabs value={selectedTab} onValueChange={(val) => setSelectedTab(val as typeof selectedTab)} className="h-full flex flex-col">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="results">Results</TabsTrigger>
                <TabsTrigger value="trends">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Trends
                </TabsTrigger>
                <TabsTrigger value="interpretation">
                  <Info className="h-4 w-4 mr-2" />
                  Interpretation
                </TabsTrigger>
              </TabsList>

              {/* Results Tab */}
              <TabsContent value="results" className="flex-1 overflow-y-auto mt-4">
                <div className="space-y-3">
                  {result.components && result.components.length > 0 ? (
                    result.components.map((component: AppTestComponent, idx: number) => (
                      <Card key={idx}>
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-medium">{component.name || 'N/A'}</h4>
                              <div className="mt-1 flex items-baseline gap-2">
                                <span className="text-2xl font-semibold">
                                  {component.resultValue ?? 'N/A'}
                                </span>
                                {component.units && (
                                  <span className="text-sm text-muted-foreground">
                                    {component.units}
                                  </span>
                                )}
                              </div>
                              {component.referenceRange && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Reference Range: {component.referenceRange}
                                </p>
                              )}
                            </div>
                            <div>
                              {getStatusBadge(component.status)}
                            </div>
                          </div>
                          {component.interpretation && (
                            <p className="mt-2 text-sm text-muted-foreground">
                              {component.interpretation}
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-center py-8">
                      No detailed component results available.
                    </p>
                  )}
                </div>
              </TabsContent>

              {/* Trends Tab */}
              <TabsContent value="trends" className="flex-1 overflow-y-auto mt-4">
                <div className="space-y-4">
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      Trend charts show how your test values have changed over time.
                      This helps identify patterns and monitor your health progress.
                    </AlertDescription>
                  </Alert>

                  {result.components && result.components.length > 0 ? (
                    result.components
                      .filter(c => c.previousValue !== undefined)
                      .map((component: AppTestComponent, idx: number) => {
                        // Mock historical data - in real app, fetch from API
                        const mockHistoricalData = [
                          {
                            date: '2024-01-01',
                            value: Number(component.previousValue) || 0,
                            referenceMin: 3.5,
                            referenceMax: 10.5
                          },
                          {
                            date: new Date().toISOString(),
                            value: Number(component.resultValue) || 0,
                            referenceMin: 3.5,
                            referenceMax: 10.5
                          }
                        ];

                        return (
                          <TestTrendsChart
                            key={idx}
                            componentName={component.name || 'Unknown'}
                            historicalData={mockHistoricalData}
                            unit={component.units || undefined}
                          />
                        );
                      })
                  ) : (
                    <p className="text-muted-foreground text-center py-8">
                      No trend data available. Historical data will appear after multiple tests.
                    </p>
                  )}
                </div>
              </TabsContent>

              {/* Interpretation Tab */}
              <TabsContent value="interpretation" className="flex-1 overflow-y-auto mt-4">
                <div className="space-y-4">
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      These explanations are simplified for general understanding.
                      Always consult your healthcare provider for personalized medical advice.
                    </AlertDescription>
                  </Alert>

                  {result.components && result.components.length > 0 ? (
                    result.components.map((component: AppTestComponent, idx: number) => (
                      <Card key={idx}>
                        <CardContent className="pt-4">
                          <h4 className="font-medium mb-2">{component.name}</h4>
                          <p className="text-sm text-muted-foreground mb-3">
                            {getSimplifiedExplanation(component)}
                          </p>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">Your Value:</span>
                            <Badge variant={component.status === 'normal' ? 'outline' : 'warning'}>
                              {component.resultValue} {component.units}
                            </Badge>
                            {component.referenceRange && (
                              <>
                                <span className="text-xs text-muted-foreground">Normal Range:</span>
                                <Badge variant="outline">{component.referenceRange}</Badge>
                              </>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-center py-8">
                      No interpretation available.
                    </p>
                  )}

                  {result.notes && (
                    <Card>
                      <CardContent className="pt-4">
                        <h4 className="font-medium mb-2">Additional Notes</h4>
                        <p className="text-sm text-muted-foreground whitespace-pre-line">
                          {result.notes}
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </ErrorBoundary>
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-between items-center bg-muted/30">
          <div className="text-sm text-muted-foreground">
            {abnormalCount === 0 ? (
              <span className="text-green-600 font-medium">All results within normal range</span>
            ) : (
              <span className="text-amber-600 font-medium">
                {abnormalCount} result{abnormalCount !== 1 ? 's' : ''} outside normal range
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPDF}>
              <Download className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default EnhancedResultDetails;
